import { styleguideColors } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { css, SerializedStyles } from '@emotion/react';
import { figmaColorScales, figmaSemanticColors, kaiTypography } from '@kaiizen/kai-shiki/core';

export type SidebarNavItemVisualState = 'default' | 'hover' | 'pressed' | 'selected';

export type IconFrameStyles = {
  frame: SerializedStyles;
  cornerBracketContainer?: SerializedStyles;
  cornerBracketBase?: SerializedStyles;
  cornerBracketTopLeft?: SerializedStyles;
  cornerBracketTopRight?: SerializedStyles;
  cornerBracketBottomLeft?: SerializedStyles;
  cornerBracketBottomRight?: SerializedStyles;
};

export type SidebarStyles = {
  root: SerializedStyles;
  logoHeading: (logoHeight: number) => SerializedStyles;
  logoButton: SerializedStyles;
  navItems: SerializedStyles;
  navItemBase: SerializedStyles;
  navItemByState: Record<SidebarNavItemVisualState, SerializedStyles>;
  iconFrame: IconFrameStyles;
  navLabelBase: SerializedStyles;
  navLabelByState: Record<SidebarNavItemVisualState, SerializedStyles>;
  footer: SerializedStyles;
};

export const DEFAULT_LOGO_HEIGHT = 60;
const SIDEBAR_WIDTH = 72;
const ICON_FRAME_SIZE = 40;
const NAV_ITEM_GAP = 14;
const BRACKET_ARM_LENGTH = 10;
const BRACKET_STROKE_WIDTH = 1;
const CORNER_BRACKET_INSET = 3;
const NAV_LABEL_FONT_FAMILY = '"Helvetica Neue", Helvetica, Arial, sans-serif';
const NAV_LABEL_FONT_SIZE = 10;

const ADMIN_NAV_COLORS = {
  background: styleguideColors.ink,
  active: styleguideColors.accent,
  icon: figmaColorScales.neutral[400],
  hoverIcon: figmaColorScales.neutral[100],
  pressedIcon: figmaColorScales.neutral[50],
  muted: figmaColorScales.neutral[400],
} as const;

const NAV_ITEM_COLOR_BY_STATE: Record<SidebarNavItemVisualState, string> = {
  default: ADMIN_NAV_COLORS.icon,
  hover: ADMIN_NAV_COLORS.hoverIcon,
  pressed: ADMIN_NAV_COLORS.pressedIcon,
  selected: ADMIN_NAV_COLORS.active,
};

// Only the label's font-weight actually varies by state (regular vs bold) —
// the rest of kaiTypography.small/smallBold's shape (fontFamily/fontSize/
// lineHeight) is deliberately overridden below by the Figma-spec'd
// Helvetica Neue 10px spec, so there's no point spreading the whole token.
const NAV_LABEL_FONT_WEIGHT_BY_STATE: Record<SidebarNavItemVisualState, number> = {
  default: kaiTypography.small.fontWeight,
  hover: kaiTypography.smallBold.fontWeight,
  pressed: kaiTypography.smallBold.fontWeight,
  selected: kaiTypography.smallBold.fontWeight,
};

export const DEFAULT_SIDEBAR_STYLES: SidebarStyles = {
  root: css`
    label: SIDEBAR_ROOT_STYLE;
    background: ${ADMIN_NAV_COLORS.background};
    width: ${pxToRem(SIDEBAR_WIDTH)};
    position: fixed;
    height: 100%;
    left: 0;
    top: 0;
    z-index: 1000;
    overflow: hidden;
  `,
  logoHeading: (logoHeight: number) =>
    css`
    label: SIDEBAR_LOGO_HEADING_STYLE;
    height: ${logoHeight}px;
    margin: 0;
  `,
  logoButton: css`
    label: SIDEBAR_LOGO_BUTTON_STYLE;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0;
    background: ${figmaSemanticColors.surface.canvas};
    cursor: pointer;
    color: ${styleguideColors.ink};
  `,
  navItems: css`
    label: SIDEBAR_NAV_ITEMS_STYLE;
    margin-top: 14px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${NAV_ITEM_GAP}px;
  `,
  navItemBase: css`
    label: SIDEBAR_NAV_ITEM_BASE_STYLE;
    width: ${ICON_FRAME_SIZE}px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 2px;
    background: transparent;
    text-decoration: none;
    transition: color 0.15s ease;
    cursor: pointer;
    appearance: none;
    border: 0;
    padding: 0;
  `,
  navItemByState: {
    default: css`
      label: SIDEBAR_NAV_ITEM_DEFAULT_STYLE;
      color: ${NAV_ITEM_COLOR_BY_STATE.default};
    `,
    hover: css`
      label: SIDEBAR_NAV_ITEM_HOVER_STYLE;
      color: ${NAV_ITEM_COLOR_BY_STATE.hover};
    `,
    pressed: css`
      label: SIDEBAR_NAV_ITEM_PRESSED_STYLE;
      color: ${NAV_ITEM_COLOR_BY_STATE.pressed};
    `,
    selected: css`
      label: SIDEBAR_NAV_ITEM_SELECTED_STYLE;
      color: ${NAV_ITEM_COLOR_BY_STATE.selected};
    `,
  },
  iconFrame: {
    frame: css`
    label: SIDEBAR_ICON_FRAME_STYLE;
    width: ${ICON_FRAME_SIZE}px;
    height: ${ICON_FRAME_SIZE}px;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  `,
    cornerBracketContainer: css`
    label: SIDEBAR_CORNER_BRACKET_CONTAINER_STYLE;
    position: absolute;
    inset: ${CORNER_BRACKET_INSET}px;
    pointer-events: none;
  `,
    cornerBracketBase: css`
    label: SIDEBAR_CORNER_BRACKET_BASE_STYLE;
    position: absolute;
    width: ${BRACKET_ARM_LENGTH}px;
    height: ${BRACKET_ARM_LENGTH}px;
    border-color: ${ADMIN_NAV_COLORS.active};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  `,
    cornerBracketTopLeft: css`
    label: SIDEBAR_CORNER_BRACKET_TOP_LEFT_STYLE;
    top: 0;
    left: 0;
    border-top-width: ${BRACKET_STROKE_WIDTH}px;
    border-left-width: ${BRACKET_STROKE_WIDTH}px;
  `,
    cornerBracketTopRight: css`
    label: SIDEBAR_CORNER_BRACKET_TOP_RIGHT_STYLE;
    top: 0;
    right: 0;
    border-top-width: ${BRACKET_STROKE_WIDTH}px;
    border-right-width: ${BRACKET_STROKE_WIDTH}px;
  `,
    cornerBracketBottomLeft: css`
    label: SIDEBAR_CORNER_BRACKET_BOTTOM_LEFT_STYLE;
    bottom: 0;
    left: 0;
    border-bottom-width: ${BRACKET_STROKE_WIDTH}px;
    border-left-width: ${BRACKET_STROKE_WIDTH}px;
  `,
    cornerBracketBottomRight: css`
    label: SIDEBAR_CORNER_BRACKET_BOTTOM_RIGHT_STYLE;
    bottom: 0;
    right: 0;
    border-bottom-width: ${BRACKET_STROKE_WIDTH}px;
    border-right-width: ${BRACKET_STROKE_WIDTH}px;
  `,
  },
  navLabelBase: css`
    label: SIDEBAR_NAV_LABEL_BASE_STYLE;
    font-family: ${NAV_LABEL_FONT_FAMILY};
    font-size: ${pxToRem(NAV_LABEL_FONT_SIZE)};
    line-height: 1;
    letter-spacing: -0.055px;
    color: currentColor;
    white-space: nowrap;
  `,
  navLabelByState: {
    default: css`
      label: SIDEBAR_NAV_LABEL_DEFAULT_STYLE;
      font-weight: ${NAV_LABEL_FONT_WEIGHT_BY_STATE.default};
    `,
    hover: css`
      label: SIDEBAR_NAV_LABEL_HOVER_STYLE;
      font-weight: ${NAV_LABEL_FONT_WEIGHT_BY_STATE.hover};
    `,
    pressed: css`
      label: SIDEBAR_NAV_LABEL_PRESSED_STYLE;
      font-weight: ${NAV_LABEL_FONT_WEIGHT_BY_STATE.pressed};
    `,
    selected: css`
      label: SIDEBAR_NAV_LABEL_SELECTED_STYLE;
      font-weight: ${NAV_LABEL_FONT_WEIGHT_BY_STATE.selected};
    `,
  },
  // kaiTypography.xxSmall's own line-height is overridden below to 16px —
  // only its fontFamily/fontSize/fontWeight/fontStyle actually apply.
  footer: css`
    label: SIDEBAR_FOOTER_STYLE;
    position: absolute;
    left: 0;
    bottom: 9px;
    width: 100%;
    font-family: ${kaiTypography.xxSmall.fontFamily};
    font-size: ${pxToRem(kaiTypography.xxSmall.fontSize)};
    font-weight: ${kaiTypography.xxSmall.fontWeight};
    font-style: ${kaiTypography.xxSmall.fontStyle};
    line-height: 1.6;
    color: ${ADMIN_NAV_COLORS.muted};
    letter-spacing: 0.2px;
    text-align: center;
  `,
};
